import { useState, useEffect } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { HELP, type HelpKey } from '@/lib/helpContent';

const SEEN_KEY = 'officina_help_seen';

/**
 * Aide contextuelle d'un écran : un « ? » à côté du titre.
 *
 * Trois partis pris, et ils tiennent au reproche qui a motivé ce travail —
 * « pas un truc overwhelming » :
 *
 *  1. **Repliée par défaut.** Qui connaît le logiciel ne voit qu'une icône
 *     grise. On n'impose rien à personne, on rend disponible.
 *  2. **Ouverte UNE fois par écran**, à la première visite seulement, puis
 *     jamais plus. C'est là que l'explication sert ; la fois d'après, elle
 *     encombre. L'état est gardé dans le navigateur, par écran.
 *  3. **Trois éléments maximum.** Une phrase pour dire à quoi sert l'écran,
 *     puis les quelques points qui ne se devinent pas — surtout les pièges.
 *     Une aide qui reprend l'évidence ne se lit plus, et emporte le reste.
 *
 * Le contenu vit dans `helpContent.tsx`, pas ici : les textes se relisent et
 * se corrigent en un seul endroit.
 */
export function PageHelp({ topic }: { topic: HelpKey }) {
  const help = HELP[topic];
  const [open, setOpen] = useState(false);

  // Première visite de CET écran : on ouvre. Les suivantes : silence.
  useEffect(() => {
    try {
      const seen = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '{}') as Record<string, boolean>;
      if (!seen[topic]) {
        setOpen(true);
        localStorage.setItem(SEEN_KEY, JSON.stringify({ ...seen, [topic]: true }));
      }
    } catch {
      // Navigation privée, stockage bloqué : on n'ouvre rien plutôt que
      // d'ouvrir à chaque fois.
    }
  }, [topic]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Masquer l\'aide' : 'À quoi sert cet écran ?'}
        title="À quoi sert cet écran ?"
        className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-full transition-colors align-middle ${
          open ? 'text-primary' : 'text-slate-300 hover:text-slate-500'
        }`}
      >
        <HelpCircle size={15} strokeWidth={2} />
      </button>

      {open && (
        <div className="mt-3 bg-primary-bg/40 border border-primary/20 rounded-xl px-4 py-3.5 relative">
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Fermer l'aide"
            className="absolute top-2.5 right-2.5 text-slate-400 hover:text-slate-700"
          >
            <X size={14} />
          </button>

          <div className="text-[12.5px] text-slate-800 leading-relaxed pr-6">{help.what}</div>

          {help.points.length > 0 && (
            <ul className="mt-2.5 space-y-1.5">
              {help.points.map((p, i) => (
                <li key={i} className="text-[12px] text-slate-600 leading-relaxed flex gap-2">
                  <span className="text-primary flex-shrink-0">·</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
